import React from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { MapPin, Phone, Mail, Star, Utensils, Coffee, Gamepad2, Building } from "lucide-react";

const VENUE_TYPE_ICONS = {
  bar: Utensils,
  cafe: Coffee,
  game_store: Gamepad2,
  community: Building,
};

const VENUE_TYPE_LABELS = {
  bar: "Bar & Restaurant",
  cafe: "Cafe",
  game_store: "Game Store",
  community: "Community Center",
};

const VenuePartnershipsSection = ({ venues }) => {
  const partnerBenefits = [
    { label: "Consistent Revenue", desc: "Earn commissions from every event hosted at your location" },
    { label: "Increased Foot Traffic", desc: "Attract new customers during traditionally slow periods" },
    { label: "Marketing Support", desc: "Featured listings on our platform with photos and descriptions" },
    { label: "Zero Management", desc: "Hosts coordinate events—you provide the space" },
  ];

  return (
    <div data-testid="venues-section">
      {/* Section Header */}
      <div className="mb-12">
        <h2 className="font-bebas text-4xl md:text-5xl tracking-wider text-white">
          PARTNER VENUES MAKING GAMING BETTER
        </h2>
        <p className="mt-4 text-[#A1A1AA] font-ubuntu text-lg max-w-3xl">
          Board2Boston™ partners with exceptional venues in your area to provide premium gaming experiences. Our partnership program benefits venues, hosts, and players alike.
        </p>
      </div>

      {/* For Venues Section */}
      <div className="mb-16 p-8 bg-[#0A0A0A] rounded-lg border border-[#7a6200]/30">
        <h3 className="font-bebas text-2xl tracking-wider text-[#7a6200] mb-6">
          FOR VENUES: JOIN OUR NETWORK
        </h3>
        <p className="text-[#A1A1AA] font-ubuntu mb-8">
          Transform your space into a gaming destination and tap into our engaged community of players.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {partnerBenefits.map((benefit, i) => (
            <div key={i} className="p-4 bg-black/30 rounded border border-white/5">
              <Star className="w-6 h-6 text-[#7a6200] mb-3" />
              <h4 className="text-white font-ubuntu font-medium mb-1">{benefit.label}</h4>
              <p className="text-[#52525B] font-ubuntu text-sm">{benefit.desc}</p>
            </div>
          ))}
        </div>

        <div className="flex flex-wrap gap-4">
          <Button className="btn-gold" data-testid="become-partner-btn">
            BECOME A PARTNER
          </Button>
          <Button className="btn-outline-gold">
            LEARN MORE
          </Button>
        </div>
      </div>

      {/* Venue Listings */}
      <h3 className="font-bebas text-2xl tracking-wider text-[#7a6200] mb-8">
        OUR PARTNER VENUES
      </h3>

      {venues.length === 0 ? (
        <div className="text-center py-16">
          <p className="text-[#52525B] font-ubuntu text-lg">Partner venues coming soon.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {venues.map((venue) => {
            const VenueIcon = VENUE_TYPE_ICONS[venue.venue_type] || Building;
            return (
              <Card 
                key={venue.id} 
                className="bg-[#0A0A0A] border border-white/5 hover:border-[#7a6200]/30 transition-colors overflow-hidden group"
                data-testid={`venue-card-${venue.id}`}
              >
                <CardContent className="p-0">
                  {/* Venue Image */}
                  {venue.image && (
                    <div className="relative h-48 overflow-hidden">
                      <img 
                        src={venue.image} 
                        alt={venue.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-transparent" />
                      <Badge className="absolute top-4 left-4 bg-[#7a6200]/80 text-white border-0">
                        <VenueIcon className="w-3 h-3 mr-1" />
                        {VENUE_TYPE_LABELS[venue.venue_type] || venue.venue_type}
                      </Badge>
                    </div>
                  )}

                  {/* Venue Info */}
                  <div className="p-6">
                    <h4 className="font-bebas text-2xl tracking-wider text-white group-hover:text-[#7a6200] transition-colors">
                      {venue.name}
                    </h4>
                    <p className="text-[#52525B] font-ubuntu text-sm mt-2 line-clamp-2">
                      {venue.description}
                    </p>

                    <div className="mt-4 space-y-2">
                      <div className="flex items-center gap-2 text-[#A1A1AA]">
                        <MapPin className="w-4 h-4 text-[#7a6200]" />
                        <span className="font-ubuntu text-sm">{venue.address}, {venue.city}</span>
                      </div>
                      <div className="flex items-center gap-2 text-[#A1A1AA]">
                        <Phone className="w-4 h-4 text-[#7a6200]" />
                        <span className="font-ubuntu text-sm">{venue.contact_phone}</span>
                      </div>
                    </div>

                    {/* Amenities */}
                    {venue.amenities && venue.amenities.length > 0 && (
                      <div className="mt-4 flex flex-wrap gap-2">
                        {venue.amenities.slice(0, 3).map((amenity, i) => (
                          <span 
                            key={i}
                            className="px-2 py-1 text-xs font-ubuntu bg-white/5 text-[#A1A1AA] rounded"
                          >
                            {amenity}
                          </span>
                        ))}
                        {venue.amenities.length > 3 && (
                          <span className="px-2 py-1 text-xs font-ubuntu text-[#52525B]">
                            +{venue.amenities.length - 3} more
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Venue Requirements */}
      <div className="mt-16 p-8 bg-[#0A0A0A] rounded-lg border border-white/5">
        <h3 className="font-bebas text-2xl tracking-wider text-[#7a6200] mb-6">
          VENUE REQUIREMENTS
        </h3>
        <p className="text-[#A1A1AA] font-ubuntu mb-6">
          We maintain quality standards to ensure excellent player experiences.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div>
            <p className="text-white font-ubuntu font-medium">Adequate Space</p>
            <p className="text-[#52525B] font-ubuntu text-sm">Room for gaming tables and seating</p>
          </div>
          <div>
            <p className="text-white font-ubuntu font-medium">Clean Environment</p>
            <p className="text-[#52525B] font-ubuntu text-sm">Well-maintained with proper lighting</p>
          </div>
          <div>
            <p className="text-white font-ubuntu font-medium">Basic Amenities</p>
            <p className="text-[#52525B] font-ubuntu text-sm">Restrooms, climate control, parking</p>
          </div>
          <div>
            <p className="text-white font-ubuntu font-medium">Insurance</p>
            <p className="text-[#52525B] font-ubuntu text-sm">Standard commercial liability coverage</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VenuePartnershipsSection;
